import React, { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import { destroyScrollbars, initializeScrollbars } from './../../composables/scrollBar.js';

function cssVariable(name, fallback) {
	const value = getComputedStyle(document.body).getPropertyValue(name).trim();
	return value || fallback;
}

function chartColors() {
	return {
		primary: cssVariable('--bs-primary', '#348fe2'),
		dark: cssVariable('--bs-dark', '#212529'),
		teal: cssVariable('--bs-teal', '#00acac'),
		indigo: cssVariable('--bs-indigo', '#8753de'),
		orange: cssVariable('--bs-orange', '#f59c1a'),
		red: cssVariable('--bs-red', '#ff5b57'),
		green: cssVariable('--bs-green', '#198754'),
		gray: cssVariable('--bs-gray', '#6c757d'),
		gray500: cssVariable('--bs-gray-500', '#adb5bd'),
		gray600: cssVariable('--bs-gray-600', '#6c757d'),
		body: cssVariable('--bs-body-color', '#6c757d'),
		border: cssVariable('--bs-border-color', 'rgba(0, 0, 0, .1)'),
		componentBg: cssVariable('--bs-component-bg', '#fff'),
		primaryRgb: cssVariable('--bs-primary-rgb', '52, 143, 226'),
		darkRgb: cssVariable('--bs-dark-rgb', '33, 37, 41'),
		white: cssVariable('--bs-white', '#fff')
	};
}

function renderChart(root, id, definition) {
	const container = root.querySelector('#' + id);
	if (!container || !definition) return null;

	const chart = new ApexCharts(container, {
		...definition,
		chart: {
			toolbar: { show: false },
			animations: { enabled: true },
			...(definition.chart || {})
		}
	});
	chart.render();
	return chart;
}

function renderMap(root, colors) {
	const container = root.querySelector('#world-map');
	if (!container) return null;

	if (container.__dashboardV1Map && container.__dashboardV1Map.destroy) {
		container.__dashboardV1Map.destroy();
	}
	container.innerHTML = '';

	const mapInstance = new jsVectorMap({
		selector: '#world-map',
		map: 'world',
		zoomButtons: false,
		zoomOnScroll: false,
		focusOn: { x: 0.5, y: 0.5, scale: 1 },
		backgroundColor: 'transparent',
		markers: [
			{ name: 'Vatican City', coords: [41.9, 12.45] },
			{ name: 'Monaco', coords: [43.73, 7.41] },
			{ name: 'Nauru', coords: [-0.52, 166.93] },
			{ name: 'Tuvalu', coords: [-8.51, 179.21] },
			{ name: 'San Marino', coords: [43.93, 12.46] },
			{ name: 'Singapore', coords: [1.3, 103.8] }
		],
		markerStyle: {
			initial: { fill: colors.teal, stroke: 'transparent', r: 3 },
			hover: { fill: colors.teal }
		},
		regionStyle: {
			initial: { fill: colors.gray600, fillOpacity: 1, stroke: 'none', strokeWidth: 0.4, strokeOpacity: 1 },
			hover: { fillOpacity: 0.8 }
		}
	});
	const destroy = () => {
		mapInstance.destroy();
		if (container.__dashboardV1Map && container.__dashboardV1Map.instance === mapInstance) {
			delete container.__dashboardV1Map;
			container.innerHTML = '';
		}
	};
	container.__dashboardV1Map = { destroy, instance: mapInstance };
	return { destroy };
}

function renderCalendar(root) {
	const container = root.querySelector('#datepicker-inline');
	if (!container) return;

	let displayDate = new Date();
	const paint = () => {
		const year = displayDate.getFullYear();
		const month = displayDate.getMonth();
		const monthName = displayDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
		const firstDay = new Date(year, month, 1).getDay();
		const daysInMonth = new Date(year, month + 1, 0).getDate();
		const previousMonthDays = new Date(year, month, 0).getDate();
		const cells = [];

		for (let index = firstDay - 1; index >= 0; index -= 1) {
			cells.push({ day: previousMonthDays - index, muted: true });
		}
		for (let day = 1; day <= daysInMonth; day += 1) {
			cells.push({
				day,
				today:
					year === new Date().getFullYear() && month === new Date().getMonth() && day === new Date().getDate()
			});
		}
		while (cells.length % 7 !== 0) {
			cells.push({ day: cells.length - firstDay - daysInMonth + 1, muted: true });
		}

		const weeks = [];
		for (let index = 0; index < cells.length; index += 7) {
			weeks.push(cells.slice(index, index + 7));
		}
		container.innerHTML =
			'<div class="datepicker-inline-calendar">' +
			'<div class="d-flex align-items-center justify-content-between px-2 py-2">' +
			'<button type="button" class="btn btn-sm btn-white" data-calendar-nav="previous" aria-label="Previous month"><i class="fa fa-chevron-left"></i></button>' +
			'<div class="fw-bold">' +
			monthName +
			'</div>' +
			'<button type="button" class="btn btn-sm btn-white" data-calendar-nav="next" aria-label="Next month"><i class="fa fa-chevron-right"></i></button>' +
			'</div>' +
			'<table class="table table-sm table-borderless text-center mb-0"><thead><tr>' +
			['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
				.map((day) => '<th class="small text-muted">' + day + '</th>')
				.join('') +
			'</tr></thead><tbody>' +
			weeks
				.map(
					(week) =>
						'<tr>' +
						week
							.map(
								(cell) =>
									'<td><button type="button" class="btn btn-sm ' +
									(cell.today ? 'btn-primary' : 'btn-white') +
									(cell.muted ? ' text-body text-opacity-25' : '') +
									'">' +
									cell.day +
									'</button></td>'
							)
							.join('') +
						'</tr>'
				)
				.join('') +
			'</tbody></table></div>';
		container.querySelector('[data-calendar-nav="previous"]').addEventListener('click', () => {
			displayDate.setMonth(displayDate.getMonth() - 1);
			paint();
		});
		container.querySelector('[data-calendar-nav="next"]').addEventListener('click', () => {
			displayDate.setMonth(displayDate.getMonth() + 1);
			paint();
		});
	};
	paint();
}

function getCharts(colors) {
	const rgba = (rgb, opacity) => 'rgba(' + rgb + ', ' + opacity + ')';
	const offsets = Array.from({ length: 77 }, (_, index) => (index === 3 ? 73 : 77 - index));
	const pageOffsets = offsets.slice(0, -2).concat([2, 0]);
	const dateSeries = (values, seriesOffsets = offsets) =>
		values.map((value, index) => {
			const date = new Date();
			date.setDate(date.getDate() - seriesOffsets[index]);
			return [date.getTime(), value];
		});
	const uniqueVisitors = [
		13, 13, 6, 6, 6, 5, 5, 5, 6, 7, 6, 9, 9, 8, 10, 10, 10, 10, 9, 9, 10, 9, 9, 8, 8, 8, 8, 8, 7, 7, 6, 6, 6, 6, 5,
		5, 4, 4, 5, 5, 5, 7, 7, 7, 10, 9, 9, 10, 11, 11, 8, 8, 7, 8, 9, 8, 9, 10, 9, 10, 16, 17, 16, 17, 16, 15, 14, 24,
		18, 15, 14, 16, 16, 17, 7, 7, 7
	];
	const pageViews = [
		14, 13, 15, 14, 13, 15, 16, 16, 14, 14, 13, 12, 13, 13, 15, 16, 16, 17, 17, 18, 15, 15, 15, 19, 19, 18, 18, 17,
		16, 18, 18, 18, 16, 14, 14, 13, 14, 13, 10, 9, 10, 11, 11, 11, 10, 9, 10, 13, 14, 14, 13, 12, 11, 13, 13, 13,
		13, 14, 13, 13, 19, 21, 22, 25, 24, 24, 22, 16, 15, 12, 12, 15, 15, 15, 18, 18, 17
	];
	const visitorSeries = [
		{ name: 'Unique Visitors', data: dateSeries(uniqueVisitors) },
		{ name: 'Page Views', data: dateSeries(pageViews, pageOffsets) }
	];

	const charts = {
		analyticChart: {
			series: visitorSeries,
			colors: [colors.dark, colors.primary],
			fill: { opacity: 0.5, type: 'solid' },
			legend: {
				position: 'top',
				floating: true,
				horizontalAlign: 'right',
				offsetY: 30,
				offsetX: 500,
				labels: { colors: colors.body }
			},
			xaxis: { type: 'datetime', tickAmount: 6, labels: { style: { colors: colors.body } } },
			yaxis: { labels: { style: { colors: colors.body } } },
			tooltip: { y: { formatter: (value) => '$ ' + value + ' thousands' } },
			chart: { height: 300, type: 'area', stacked: true, zoom: { enabled: false } },
			plotOptions: { bar: { horizontal: false, columnWidth: '55%', endingShape: 'rounded' } },
			dataLabels: { enabled: false },
			grid: {
				show: true,
				borderColor: colors.border,
				xaxis: { lines: { show: true } },
				yaxis: { lines: { show: true } },
				padding: { top: -10, right: 10, bottom: 0, left: 10 }
			},
			stroke: { show: true, width: 2, curve: 'straight' }
		},
		'donut-chart': {
			series: [35, 30, 15, 10, 5],
			chart: { type: 'donut', height: 200 },
			colors: [
				rgba(colors.primaryRgb, 0.75),
				colors.primary,
				rgba(colors.primaryRgb, 0.5),
				colors.dark,
				rgba(colors.darkRgb, 0.75)
			],
			labels: ['Chrome', 'Firefox', 'Safari', 'Opera', 'IE'],
			plotOptions: { pie: { donut: { size: '45%', background: 'transparent' } } },
			dataLabels: {
				enabled: true,
				formatter: (value, opts) => opts.w.config.labels[opts.seriesIndex] + ': ' + Math.round(value) + '%',
				dropShadow: { enabled: false }
			},
			stroke: { width: 2, colors: [colors.componentBg] },
			legend: { position: 'bottom', show: true },
			tooltip: { y: { formatter: (value) => value.toFixed(2) + '%' } }
		}
	};

	const sparklineData = [50, 30, 45, 40, 50, 20, 35, 40, 50, 70, 90, 40];
	const sparkline = (id, color) => ({
		series: [{ name: id.replace('sparkline-', '').replaceAll('-', ' '), data: sparklineData }],
		chart: { type: 'line', height: 23, width: 100, sparkline: { enabled: true }, animations: { enabled: false } },
		stroke: { width: 2, curve: 'straight', colors: [color] },
		colors: [color],
		markers: { size: 0, colors: [color], strokeColors: color, hover: { size: 5 } },
		tooltip: {
			fixed: { enabled: false },
			x: { show: false },
			y: { title: { formatter: () => '' } },
			marker: { show: true }
		},
		xaxis: { type: 'category', labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
		yaxis: { min: 0, max: 100, labels: { show: false } },
		grid: { show: false, padding: { left: 0, right: 0, top: 0, bottom: 0 } }
	});
	charts['sparkline-unique-visitor'] = sparkline('sparkline-unique-visitor', colors.red);
	charts['sparkline-bounce-rate'] = sparkline('sparkline-bounce-rate', colors.orange);
	charts['sparkline-total-page-views'] = sparkline('sparkline-total-page-views', colors.green);
	charts['sparkline-avg-time-on-site'] = sparkline('sparkline-avg-time-on-site', colors.primary);
	charts['sparkline-new-visits'] = sparkline('sparkline-new-visits', colors.gray);
	charts['sparkline-return-visitors'] = sparkline('sparkline-return-visitors', colors.dark);
	return charts;
}

function wireTabs(root) {
	root.querySelectorAll('[data-bs-toggle="tab"]').forEach((tab) => {
		tab.addEventListener('click', (event) => {
			event.preventDefault();
			const targetId = tab.getAttribute('href');
			if (!targetId || !targetId.startsWith('#')) return;
			root.querySelectorAll('[data-bs-toggle="tab"]').forEach((item) =>
				item.classList.toggle('active', item === tab)
			);
			root.querySelectorAll('.tab-pane').forEach((pane) =>
				pane.classList.toggle('show', '#' + pane.id === targetId)
			);
			root.querySelectorAll('.tab-pane').forEach((pane) =>
				pane.classList.toggle('active', '#' + pane.id === targetId)
			);
		});
	});
}

function wireCardActions(root) {
	root.querySelectorAll('a[href="#/"], a[href="#/"]').forEach((link) => {
		link.addEventListener('click', (event) => event.preventDefault());
	});

	root.querySelectorAll('[data-toggle="card-expand"]').forEach((button) => {
		button.addEventListener('click', (event) => {
			event.preventDefault();
			button.closest('.card')?.classList.toggle('card-expand');
		});
	});
	root.querySelectorAll('[data-toggle="card-collapse"]').forEach((button) => {
		button.addEventListener('click', (event) => {
			event.preventDefault();
			const card = button.closest('.card');
			if (!card) return;
			Array.from(card.children)
				.filter((child) => !child.classList.contains('card-header'))
				.forEach((child) => child.classList.toggle('d-none'));
		});
	});
	root.querySelectorAll('[data-toggle="card-remove"]').forEach((button) => {
		button.addEventListener('click', (event) => {
			event.preventDefault();
			button.closest('.card')?.remove();
		});
	});
	root.querySelectorAll('[data-toggle="card-reload"]').forEach((button) => {
		button.addEventListener('click', (event) => {
			event.preventDefault();
			const card = button.closest('.card');
			const body = card?.querySelector('.card-body');
			if (!card || !body) return;
			card.classList.add('card-loading');
			const loader = document.createElement('div');
			loader.className = 'card-loader';
			loader.innerHTML = '<span class="spinner spinner-sm"></span>';
			body.appendChild(loader);
			window.setTimeout(() => {
				loader.remove();
				card.classList.remove('card-loading');
				window.dispatchEvent(new Event('resize'));
			}, 800);
		});
	});
}

function renderDashboard(root) {
	const colors = chartColors();
	const definitions = getCharts(colors);
	const charts = Object.keys(definitions)
		.map((id) => renderChart(root, id, definitions[id]))
		.filter(Boolean);
	const map = renderMap(root, colors);
	const scrollbars = initializeScrollbars(root);
	renderCalendar(root);
	wireTabs(root);
	wireCardActions(root);

	return () => {
		charts.forEach((chart) => chart.destroy());
		map?.destroy?.();
		destroyScrollbars(scrollbars);
	};
}

function DashboardV1() {
	const rootRef = useRef(null);
	const [themeVersion, setThemeVersion] = useState(0);

	useEffect(() => {
		if (!rootRef.current) return undefined;
		const cleanup = renderDashboard(rootRef.current);
		const refresh = () => setThemeVersion((current) => current + 1);
		document.addEventListener('theme-reload', refresh);
		return () => {
			document.removeEventListener('theme-reload', refresh);
			cleanup?.();
		};
	}, [themeVersion]);

	return (
		<div ref={rootRef}>
			{/* BEGIN breadcrumb */}
			<ol className="breadcrumb float-xl-end">
				<li className="breadcrumb-item">
					<a href="#/">Home</a>
				</li>
				<li className="breadcrumb-item active">Dashboard</li>
			</ol>
			{/* END breadcrumb */}
			{/* BEGIN page-header */}
			<h1 className="page-header">
				Dashboard <small>page header description goes here...</small>
			</h1>
			{/* END page-header */}
			{/* BEGIN row */}
			<div className="row mb-3">
				{/* BEGIN col-3 */}
				<div className="col-xl-3 col-md-6">
					<div className="card card-borderless rounded-3 overflow-hidden bg-teal" data-bs-theme="dark">
						<div className="card-body position-relative z-3">
							<div className="fw-bold text-white small mb-1 d-flex align-items-center gap-2">
								<iconify-icon icon="solar:user-bold-duotone" className="fs-6"></iconify-icon> Total
								Visitors
							</div>
							<div className="fw-bold fs-2 text-white">3,291,922</div>
							<div className="fw-semibold text-white text-opacity-75 small mb-3 text-ellipsis">
								+50% compare to last 7 days
							</div>
							<div className="text-end mb-n3 mx-n3">
								<a
									href="#/"
									className="text-decoration-none link-white fw-semibold small d-block px-3 py-2 bg-black bg-opacity-50"
								>
									View Detail <i className="fa fa-chevron-right"></i>
								</a>
							</div>
						</div>
						<div className="position-absolute top-0 end-0 mt-n5 ps-5 w-25">
							<div className="w-250px h-250px bg-black bg-opacity-25 rounded-circle ms-n5 position-absolute top-0 start-0"></div>
							<div className="w-250px h-250px bg-black bg-opacity-25 rounded-circle mt-n5 position-absolute top-0 start-0"></div>
						</div>
					</div>
				</div>
				{/* END col-3 */}
				{/* BEGIN col-3 */}
				<div className="col-xl-3 col-md-6 mt-3 mt-md-0">
					<div className="card card-borderless rounded-3 overflow-hidden bg-blue" data-bs-theme="dark">
						<div className="card-body position-relative z-3">
							<div className="fw-bold text-white small mb-1 d-flex align-items-center gap-2">
								<iconify-icon icon="solar:eye-bold-duotone" className="fs-6"></iconify-icon> Bounce Rate
							</div>
							<div className="fw-bold fs-2 text-white">20.44%</div>
							<div className="fw-semibold text-white text-opacity-75 small mb-3 text-ellipsis">
								-12.3% compare to last 7 days
							</div>
							<div className="text-end mb-n3 mx-n3">
								<a
									href="#/"
									className="text-decoration-none link-white fw-semibold small d-block px-3 py-2 bg-black bg-opacity-50"
								>
									View Detail <i className="fa fa-chevron-right"></i>
								</a>
							</div>
						</div>
						<div className="position-absolute top-0 end-0 mt-n5 ps-5 w-25">
							<div className="w-250px h-250px bg-black bg-opacity-25 rounded-circle ms-n5 position-absolute top-0 start-0"></div>
							<div className="w-250px h-250px bg-black bg-opacity-25 rounded-circle mt-n5 position-absolute top-0 start-0"></div>
						</div>
					</div>
				</div>
				{/* END col-3 */}
				{/* BEGIN col-3 */}
				<div className="col-xl-3 col-md-6 mt-3 mt-xl-0">
					<div className="card card-borderless rounded-3 overflow-hidden bg-purple" data-bs-theme="dark">
						<div className="card-body position-relative z-3">
							<div className="fw-bold text-white small mb-1 d-flex align-items-center gap-2">
								<iconify-icon icon="solar:mouse-bold-duotone" className="fs-6"></iconify-icon> Unique
								Visitors
							</div>
							<div className="fw-bold fs-2 text-white">1,291,922</div>
							<div className="fw-semibold text-white text-opacity-75 small mb-3 text-ellipsis">
								+5.9% compare to last 7 days
							</div>
							<div className="text-end mb-n3 mx-n3">
								<a
									href="#/"
									className="text-decoration-none link-white fw-semibold small d-block px-3 py-2 bg-black bg-opacity-50"
								>
									View Detail <i className="fa fa-chevron-right"></i>
								</a>
							</div>
						</div>
						<div className="position-absolute top-0 end-0 mt-n5 ps-5 w-25">
							<div className="w-250px h-250px bg-black bg-opacity-25 rounded-circle ms-n5 position-absolute top-0 start-0"></div>
							<div className="w-250px h-250px bg-black bg-opacity-25 rounded-circle mt-n5 position-absolute top-0 start-0"></div>
						</div>
					</div>
				</div>
				{/* END col-3 */}
				{/* BEGIN col-3 */}
				<div className="col-xl-3 col-md-6 mt-3 mt-xl-0">
					<div className="card card-borderless rounded-3 overflow-hidden bg-gray-900" data-bs-theme="dark">
						<div className="card-body position-relative z-3">
							<div className="fw-bold text-white small mb-1 d-flex align-items-center gap-2">
								<iconify-icon icon="solar:clock-circle-bold-duotone" className="fs-6"></iconify-icon>{' '}
								Avg Time on Site
							</div>
							<div className="fw-bold fs-2 text-white">00:12:23</div>
							<div className="fw-semibold text-white text-opacity-75 small mb-3 text-ellipsis">
								+00:01:88 compare to last 7 days
							</div>
							<div className="text-end mb-n3 mx-n3">
								<a
									href="#/"
									className="text-decoration-none link-white fw-semibold small d-block px-3 py-2 bg-black bg-opacity-50"
								>
									View Detail <i className="fa fa-chevron-right"></i>
								</a>
							</div>
						</div>
						<div className="position-absolute top-0 end-0 mt-n5 ps-5 w-25">
							<div className="w-250px h-250px bg-white bg-opacity-10 rounded-circle ms-n5 position-absolute top-0 start-0"></div>
							<div className="w-250px h-250px bg-white bg-opacity-20 rounded-circle mt-n5 position-absolute top-0 start-0"></div>
						</div>
					</div>
				</div>
				{/* END col-3 */}
			</div>
			{/* END row */}
			{/* BEGIN row */}
			<div className="row g-3">
				{/* BEGIN col-8 */}
				<div className="col-xl-8 d-flex flex-column gap-3">
					{/* BEGIN card */}
					<div className="card card-borderless">
						<div className="card-header bg-gray-900" data-bs-theme="dark">
							<h4 className="card-header-title text-white d-flex align-items-center gap-3">
								<span>Website Analytics</span>
								<select className="form-select form-select-sm h-25px py-0 w-100px border-0 shadow-none my-n1 bg-dark">
									<option>7 Days</option>
									<option>30 Days</option>
									<option>90 Days</option>
								</select>
							</h4>
							<div className="card-header-btn">
								<a href="#/" className="btn btn-xs btn-icon btn-default" data-toggle="card-expand">
									<i className="fa fa-expand"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-success" data-toggle="card-reload">
									<i className="fa fa-redo"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-warning" data-toggle="card-collapse">
									<i className="fa fa-minus"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-danger" data-toggle="card-remove">
									<i className="fa fa-times"></i>
								</a>
							</div>
						</div>
						<div className="card-body pb-0">
							<div className="row g-3">
								<div className="col-lg-3 col-md-6">
									<div className="rounded bg-primary bg-opacity-15 p-3 position-relative">
										<div className="small fw-semibold">Active users</div>
										<div className="fw-bold fs-4">360,483</div>
										<div className="small fw-semibold d-flex align-items-center">
											<i className="fa fa-caret-up text-primary"></i> 122.1%
										</div>
										<div className="w-50px h-50px fs-2 d-flex align-items-center justify-content-center position-absolute top-0 end-0 text-primary">
											<iconify-icon icon="solar:user-bold-duotone"></iconify-icon>
										</div>
									</div>
								</div>
								<div className="col-lg-3 col-md-6">
									<div className="rounded bg-primary bg-opacity-15 p-3 position-relative">
										<div className="small fw-semibold">Event count</div>
										<div className="fw-bold fs-4">1.35m</div>
										<div className="small fw-semibold d-flex align-items-center">
											<i className="fa fa-caret-up text-primary"></i> 29.9%
										</div>
										<div className="w-50px h-50px fs-2 d-flex align-items-center justify-content-center position-absolute top-0 end-0 text-primary">
											<iconify-icon icon="solar:calendar-add-bold-duotone"></iconify-icon>
										</div>
									</div>
								</div>
								<div className="col-lg-3 col-md-6">
									<div className="rounded bg-primary bg-opacity-15 p-3 position-relative">
										<div className="small fw-semibold">Avg Session</div>
										<div className="fw-bold fs-4">3m 42s</div>
										<div className="small fw-semibold d-flex align-items-center">
											<i className="fa fa-caret-down text-primary"></i> 0.25s
										</div>
										<div className="w-50px h-50px fs-2 d-flex align-items-center justify-content-center position-absolute top-0 end-0 text-primary">
											<iconify-icon icon="solar:clock-circle-bold-duotone"></iconify-icon>
										</div>
									</div>
								</div>
								<div className="col-lg-3 col-md-6">
									<div className="rounded bg-primary bg-opacity-15 p-3 position-relative">
										<div className="small fw-semibold">Bounce Rate</div>
										<div className="fw-bold fs-4">2.8%</div>
										<div className="small fw-semibold d-flex align-items-center">
											<i className="fa fa-caret-up text-primary"></i> 1.94%
										</div>
										<div className="w-50px h-50px fs-2 d-flex align-items-center justify-content-center position-absolute top-0 end-0 text-primary">
											<iconify-icon icon="solar:link-broken-minimalistic-bold-duotone"></iconify-icon>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div id="analyticChart" className="h-300px pe-2 pt-1"></div>
					</div>
					{/* END card */}
					{/* BEGIN card */}
					<div className="card card-borderless">
						<ul className="nav nav-tabs nav-tabs-inverse nav-justified rounded-top border-0">
							<li className="nav-item">
								<a
									href="#latestActivities"
									data-bs-toggle="tab"
									className="nav-link py-10px border-0 active d-flex align-items-center justify-content-center gap-2"
								>
									<iconify-icon
										icon="solar:checklist-minimalistic-bold-duotone"
										className="fs-4 text-primary"
									></iconify-icon>
									<span className="d-none d-md-inline">Latest Activities</span>
								</a>
							</li>
							<li className="nav-item">
								<a
									href="#recentOrders"
									data-bs-toggle="tab"
									className="nav-link py-10px border-0 d-flex align-items-center justify-content-center gap-2"
								>
									<iconify-icon
										icon="solar:bag-smile-bold-duotone"
										className="fs-4 text-primary"
									></iconify-icon>
									<span className="d-none d-md-inline">Recent Orders</span>
								</a>
							</li>
							<li className="nav-item">
								<a
									href="#messages"
									data-bs-toggle="tab"
									className="nav-link py-10px border-0 d-flex align-items-center justify-content-center gap-2"
								>
									<iconify-icon
										icon="solar:chat-line-bold-duotone"
										className="fs-4 text-primary"
									></iconify-icon>
									<span className="d-none d-md-inline">Messages</span>
								</a>
							</li>
						</ul>
						<div className="tab-content rounded-0 rounded-bottom">
							<div className="tab-pane fade active show" id="latestActivities">
								<div className="h-350px p-3" data-scrollbar="true" data-wheel-propagation="true">
									<div className="d-sm-flex gap-3">
										<div className="w-sm-100px w-lg-200px">
											<img
												className="w-100 rounded-3"
												src="/color-admin/admin/react/assets/img/dashboard/post-1.jpg"
												alt=""
											/>
										</div>
										<div className="flex-1">
											<h5 className="mb-2">Quarterly Planning Meeting Completed</h5>
											<div className="small fw-semibold text-muted mb-2 d-flex align-items-center gap-2">
												<span>
													<i className="far fa-clock"></i> 2 hours ago
												</span>
												<span>
													<i className="fas fa-users ms-2"></i> 12 participants
												</span>
											</div>
											<p className="mb-3 small">
												{' '}
												The leadership team successfully concluded the Q3 strategic planning
												session. Key decisions were made regarding product roadmap expansion and
												resource allocation for upcoming projects. Action items have been
												assigned to department heads.{' '}
											</p>
											<div className="d-flex flex-wrap gap-2">
												<span className="badge bg-light text-dark px-8px py-6px rounded-1">
													<i className="fa fa-tag fa-lg me-1"></i>Planning
												</span>
												<span className="badge bg-light text-dark px-8px py-6px rounded-1">
													<i className="fa fa-tag fa-lg me-1"></i>Strategy
												</span>
												<span className="badge bg-light text-dark px-8px py-6px rounded-1">
													<i className="fa fa-tag fa-lg me-1"></i>Leadership
												</span>
											</div>
										</div>
									</div>
									<hr className="my-3" />
									<div className="d-sm-flex gap-3">
										<div className="w-sm-100px w-lg-200px">
											<img
												className="w-100 rounded-3"
												src="/color-admin/admin/react/assets/img/dashboard/post-2.jpg"
												alt=""
											/>
										</div>
										<div className="flex-1">
											<h5 className="mb-2">Mobile App v3.5 Successfully Deployed</h5>
											<div className="small fw-semibold text-muted mb-2 d-flex align-items-center gap-2">
												<span>
													<i className="far fa-clock"></i> 5 hours ago
												</span>
												<span>
													<i className="fas fa-users ms-2"></i> iOS & Android
												</span>
											</div>
											<p className="mb-3 small">
												{' '}
												Version 3.5 of our mobile application is now live with enhanced security
												features and improved user interface. The update includes biometric
												authentication and offline mode capabilities, receiving positive early
												feedback from beta testers.{' '}
											</p>
											<div className="d-flex flex-wrap gap-2">
												<span className="badge bg-light text-dark px-8px py-6px rounded-1">
													<i className="fa fa-tag fa-lg me-1"></i>Mobile
												</span>
												<span className="badge bg-light text-dark px-8px py-6px rounded-1">
													<i className="fa fa-tag fa-lg me-1"></i>Release
												</span>
												<span className="badge bg-light text-dark px-8px py-6px rounded-1">
													<i className="fa fa-tag fa-lg me-1"></i>Update
												</span>
											</div>
										</div>
									</div>
									<hr className="my-3" />
									<div className="d-sm-flex gap-3">
										<div className="w-sm-100px w-lg-200px">
											<img
												className="w-100 rounded-3"
												src="/color-admin/admin/react/assets/img/dashboard/post-3.jpg"
												alt=""
											/>
										</div>
										<div className="flex-1">
											<h5 className="mb-2">Customer Satisfaction Reaches 94%</h5>
											<div className="small fw-semibold text-muted mb-2 d-flex align-items-center gap-2">
												<span>
													<i className="far fa-clock"></i> Yesterday
												</span>
												<span>
													<i className="fas fa-users ms-2"></i> +3% increase
												</span>
											</div>
											<p className="mb-3 small">
												{' '}
												Latest customer feedback survey shows satisfaction rating climbed to
												94%, the highest in company history. Support response times improved by
												40% and resolution rate reached 89% within first contact.{' '}
											</p>
											<div className="d-flex flex-wrap gap-2">
												<span className="badge bg-light text-dark px-8px py-6px rounded-1">
													<i className="fa fa-tag fa-lg me-1"></i>Customer
												</span>
												<span className="badge bg-light text-dark px-8px py-6px rounded-1">
													<i className="fa fa-tag fa-lg me-1"></i>Metrics
												</span>
												<span className="badge bg-light text-dark px-8px py-6px rounded-1">
													<i className="fa fa-tag fa-lg me-1"></i>Support
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="tab-pane fade" id="recentOrders">
								<div className="h-350px" data-scrollbar="true">
									<div className="table-responsive">
										<table className="table table-card mb-0 text-nowrap">
											<thead className="table-py-10px">
												<tr>
													<th>Date & Time</th>
													<th>Product</th>
													<th>Customer</th>
													<th>Status</th>
													<th className="text-end">Amount</th>
												</tr>
											</thead>
											<tbody className="table-py-15px">
												<tr>
													<td>
														<div className="fw-semibold">Today, 10:30 AM</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Order #ORD-7842
														</div>
													</td>
													<td>
														<div className="d-flex align-items-center gap-2">
															<img
																src="/color-admin/admin/react/assets/img/product/apple-watch-se-3.jpg"
																alt=""
																className="rounded"
																width="32"
															/>
															<div>
																<div className="fw-semibold">Apple Watch SE 3</div>
																<div className="fw-semibold fs-11px text-body text-opacity-50">
																	Watch
																</div>
															</div>
														</div>
													</td>
													<td>
														<div className="fw-semibold">Alex Johnson</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															New York, NY
														</div>
													</td>
													<td>
														<span className="badge bg-success bg-opacity-15 text-success py-6px">
															Delivered
														</span>
													</td>
													<td className="text-end fw-bold">$249.99</td>
												</tr>
												<tr>
													<td>
														<div className="fw-semibold">Yesterday, 2:15 PM</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Order #ORD-7841
														</div>
													</td>
													<td>
														<div className="d-flex align-items-center gap-2">
															<img
																src="/color-admin/admin/react/assets/img/product/iphone-17-pro-max.jpg"
																alt=""
																className="rounded"
																width="32"
															/>
															<div>
																<div className="fw-semibold">iPhone 17 Pro Max</div>
																<div className="fw-semibold fs-11px text-body text-opacity-50">
																	phone
																</div>
															</div>
														</div>
													</td>
													<td>
														<div className="fw-semibold">Maria Garcia</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Miami, FL
														</div>
													</td>
													<td>
														<span className="badge bg-warning bg-opacity-15 text-warning py-6px">
															Shipped
														</span>
													</td>
													<td className="text-end fw-bold">$199.99</td>
												</tr>
												<tr>
													<td>
														<div className="fw-semibold">Mar 15, 9:45 AM</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Order #ORD-7840
														</div>
													</td>
													<td>
														<div className="d-flex align-items-center gap-2">
															<img
																src="/color-admin/admin/react/assets/img/product/ipad-pro.jpg"
																alt=""
																className="rounded"
																width="32"
															/>
															<div>
																<div className="fw-semibold">iPad Pro</div>
																<div className="fw-semibold fs-11px text-body text-opacity-50">
																	tablet
																</div>
															</div>
														</div>
													</td>
													<td>
														<div className="fw-semibold">David Chen</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															San Francisco, CA
														</div>
													</td>
													<td>
														<span className="badge bg-light text-dark">Processing</span>
													</td>
													<td className="text-end fw-bold">$89.99</td>
												</tr>
												<tr>
													<td>
														<div className="fw-semibold">Mar 14, 4:20 PM</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Order #ORD-7839
														</div>
													</td>
													<td>
														<div className="d-flex align-items-center gap-2">
															<img
																src="/color-admin/admin/react/assets/img/product/apple-watch-ultra-3.jpg"
																alt=""
																className="rounded"
																width="32"
															/>
															<div>
																<div className="fw-semibold">Apple Watch Ultra 3</div>
																<div className="fw-semibold fs-11px text-body text-opacity-50">
																	Watch
																</div>
															</div>
														</div>
													</td>
													<td>
														<div className="fw-semibold">Sarah Miller</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Chicago, IL
														</div>
													</td>
													<td>
														<span className="badge bg-danger bg-opacity-15 text-danger py-6px">
															Cancelled
														</span>
													</td>
													<td className="text-end fw-bold">$129.99</td>
												</tr>
												<tr>
													<td>
														<div className="fw-semibold">Mar 13, 11:10 AM</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Order #ORD-7838
														</div>
													</td>
													<td>
														<div className="d-flex align-items-center gap-2">
															<img
																src="/color-admin/admin/react/assets/img/product/ipad-air.jpg"
																alt=""
																className="rounded"
																width="32"
															/>
															<div>
																<div className="fw-semibold">iPad Air</div>
																<div className="fw-semibold fs-11px text-body text-opacity-50">
																	tablet
																</div>
															</div>
														</div>
													</td>
													<td>
														<div className="fw-semibold">James Wilson</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Austin, TX
														</div>
													</td>
													<td>
														<span className="badge bg-success bg-opacity-15 text-success py-6px">
															Delivered
														</span>
													</td>
													<td className="text-end fw-bold">$149.99</td>
												</tr>
												<tr>
													<td>
														<div className="fw-semibold">Mar 14, 12:09 AM</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Order #ORD-7837
														</div>
													</td>
													<td>
														<div className="d-flex align-items-center gap-2">
															<img
																src="/color-admin/admin/react/assets/img/product/apple-watch-series-11.jpg"
																alt=""
																className="rounded"
																width="32"
															/>
															<div>
																<div className="fw-semibold">Apple Watch Series 11</div>
																<div className="fw-semibold fs-11px text-body text-opacity-50">
																	watch
																</div>
															</div>
														</div>
													</td>
													<td>
														<div className="fw-semibold">James Wilson</div>
														<div className="fw-semibold fs-11px text-body text-opacity-50">
															Austin, TX
														</div>
													</td>
													<td>
														<span className="badge bg-success bg-opacity-15 text-success py-6px">
															Delivered
														</span>
													</td>
													<td className="text-end fw-bold">$149.99</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</div>
							<div className="tab-pane fade" id="messages">
								<div className="h-350px p-3" data-scrollbar="true">
									<div className="d-flex gap-3 mb-3">
										<div>
											<img
												src="/color-admin/admin/react/assets/img/user/user-15.jpg"
												alt=""
												className="rounded-circle"
												width="48"
											/>
										</div>
										<div className="flex-1">
											<div className="d-flex mb-2">
												<div className="flex-1">
													<h6 className="mb-0 fw-bold">Jennifer Lee</h6>
													<div className="small fw-semibold text-muted d-flex align-items-center flex-wrap gap-2">
														<span>Project Manager</span>
														<i className="fa fa-circle fs-4px text-body text-opacity-25"></i>
														<span>TechCorp Inc.</span>
													</div>
												</div>
												<div>
													<a
														href="#/"
														className="link-secondary text-decoration-none btn border-0"
														data-bs-toggle="dropdown"
													>
														<i className="fas fa-ellipsis-h fa-lg"></i>
													</a>
													<div className="dropdown-menu">
														<a href="#/" className="dropdown-item">
															View
														</a>
														<a href="#/" className="dropdown-item">
															Reply
														</a>
														<a href="#/" className="dropdown-item">
															Forward
														</a>
														<a href="#/" className="dropdown-item">
															View attachment
														</a>
														<div className="dropdown-divider"></div>
														<a href="#/" className="dropdown-item text-danger">
															Remove
														</a>
													</div>
												</div>
											</div>
											<div className="bg-light p-3 rounded mb-2">
												<h6 className="mb-1">Partnership Proposal for Q3 Collaboration</h6>
												<div className="text-muted small">
													{' '}
													Following up on our discussion about potential collaboration
													opportunities for the upcoming quarter. I've attached the initial
													proposal document outlining how our teams can work together on the
													new mobile platform. Looking forward to scheduling a brief call next
													week to discuss further.{' '}
												</div>
											</div>
											<div className="text-muted small">
												<i className="far fa-clock me-1"></i> Received 2 hours ago
											</div>
										</div>
									</div>
									<hr className="my-3" />
									<div className="d-flex gap-3 mb-3">
										<div>
											<img
												src="/color-admin/admin/react/assets/img/user/user-16.jpg"
												alt=""
												className="rounded-circle"
												width="48"
											/>
										</div>
										<div className="flex-1">
											<div className="d-flex mb-2">
												<div className="flex-1">
													<h6 className="mb-0 fw-bold">Robert Davis</h6>
													<div className="small fw-semibold text-muted d-flex align-items-center flex-wrap gap-2">
														<span>CTO</span>
														<i className="fa fa-circle fs-4px text-body text-opacity-25"></i>
														<span>Startup Innovations</span>
													</div>
												</div>
												<div>
													<a
														href="#/"
														className="link-secondary text-decoration-none btn border-0"
														data-bs-toggle="dropdown"
													>
														<i className="fas fa-ellipsis-h fa-lg"></i>
													</a>
													<div className="dropdown-menu">
														<a href="#/" className="dropdown-item">
															View
														</a>
														<a href="#/" className="dropdown-item">
															Reply
														</a>
														<a href="#/" className="dropdown-item">
															Forward
														</a>
														<a href="#/" className="dropdown-item">
															View attachment
														</a>
														<div className="dropdown-divider"></div>
														<a href="#/" className="dropdown-item text-danger">
															Remove
														</a>
													</div>
												</div>
											</div>
											<div className="bg-light p-3 rounded mb-2">
												<h6 className="mb-1">Technical Integration Meeting Follow-up</h6>
												<div className="text-muted small">
													Thank you for the productive meeting yesterday. As discussed, our
													technical team has prepared the API documentation for integration.
													We've identified three key endpoints that need your review before we
													proceed with implementation. Please let me know when would be a good
													time to walk through the technical specifications.
												</div>
											</div>
											<div className="text-muted small">
												<i className="far fa-clock me-1"></i> Received yesterday
											</div>
										</div>
									</div>
									<hr className="my-3" />
									<div className="d-flex gap-3 mb-3">
										<div>
											<img
												src="/color-admin/admin/react/assets/img/user/user-14.jpg"
												alt=""
												className="rounded-circle"
												width="48"
											/>
										</div>
										<div className="flex-1">
											<div className="d-flex mb-2">
												<div className="flex-1">
													<h6 className="mb-0 fw-bold">Amanda Rodriguez</h6>
													<div className="small fw-semibold text-muted d-flex align-items-center flex-wrap gap-2">
														<span>Marketing Director</span>
														<i className="fa fa-circle fs-4px text-body text-opacity-25"></i>
														<span>Global Brands</span>
													</div>
												</div>
												<div>
													<a
														href="#/"
														className="link-secondary text-decoration-none btn border-0"
														data-bs-toggle="dropdown"
													>
														<i className="fas fa-ellipsis-h fa-lg"></i>
													</a>
													<div className="dropdown-menu">
														<a href="#/" className="dropdown-item">
															View
														</a>
														<a href="#/" className="dropdown-item">
															Reply
														</a>
														<a href="#/" className="dropdown-item">
															Forward
														</a>
														<a href="#/" className="dropdown-item">
															View attachment
														</a>
														<div className="dropdown-divider"></div>
														<a href="#/" className="dropdown-item text-danger">
															Remove
														</a>
													</div>
												</div>
											</div>
											<div className="bg-light p-3 rounded mb-2">
												<h6 className="mb-1">Campaign Performance Report - March</h6>
												<div className="text-muted small">
													I'm pleased to share that our recent digital campaign exceeded
													expectations with a 37% increase in engagement and 24% higher
													conversion rates compared to last month. The attached report details
													the performance metrics across all channels. Let's schedule a quick
													sync to discuss optimization strategies for April.
												</div>
											</div>
											<div className="text-muted small">
												<i className="far fa-clock me-1"></i> Received 2 days ago
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					{/* END card */}
					{/* BEGIN card */}
					<div className="card card-borderless">
						<div className="card-header bg-gray-900">
							<h4 className="card-header-title text-white">Quick Post</h4>
							<div className="card-header-btn">
								<a href="#/" className="btn btn-xs btn-icon btn-default" data-toggle="card-expand">
									<i className="fa fa-expand"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-success" data-toggle="card-reload">
									<i className="fa fa-redo"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-warning" data-toggle="card-collapse">
									<i className="fa fa-minus"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-danger" data-toggle="card-remove">
									<i className="fa fa-times"></i>
								</a>
							</div>
						</div>
						{/* user bar */}
						<div className="d-flex align-items-center px-3 py-2 border-bottom gap-2">
							<div className="position-relative">
								<img src="/color-admin/admin/react/assets/img/user/user-16.jpg" width="42" className="rounded-circle me-1"  alt="" />
							</div>
							<div className="flex-1">
								<div className="fw-bold">Sean Ngu</div>
								<div className="fw-semibold small text-muted lh-sm">Posting publicly</div>
							</div>
							<span className="badge bg-light text-dark py-8px px-10px fw-bold d-flex align-items-center gap-1">
								<i className="fa fa-circle fs-5px text-success"></i> Online
							</span>
						</div>
						{/* toolbar */}
						<div className="px-3 py-2 border-bottom">
							<div className="btn-group me-2">
								<button className="btn btn-white btn-sm">
									<i className="fa fa-bold"></i>
								</button>
								<button className="btn btn-white btn-sm">
									<i className="fa fa-italic"></i>
								</button>
								<button className="btn btn-white btn-sm">
									<i className="fa fa-underline"></i>
								</button>
							</div>
							<div className="btn-group me-2">
								<button className="btn btn-white btn-sm">
									<i className="fa fa-align-left"></i>
								</button>
								<button className="btn btn-white btn-sm active">
									<i className="fa fa-align-center"></i>
								</button>
								<button className="btn btn-white btn-sm">
									<i className="fa fa-align-right"></i>
								</button>
								<button className="btn btn-white btn-sm">
									<i className="fa fa-align-justify"></i>
								</button>
							</div>
							<div className="btn-group ms-auto">
								<button className="btn btn-white btn-sm">
									<i className="fa fa-image"></i>
								</button>
								<button className="btn btn-white btn-sm">
									<i className="fa fa-link"></i>
								</button>
								<button className="btn btn-white btn-sm">
									<i className="fa fa-smile"></i>
								</button>
							</div>
						</div>
						{/* textarea */}
						<textarea
							className="form-control rounded-0 border-0 shadow-none bg-light p-3"
							rows="10"
							placeholder="What's on your mind?"
						></textarea>
						{/* footer */}
						<div className="d-flex align-items-center px-3 py-2 border-top">
							<select className="form-select form-select-sm w-auto me-3">
								<option>Public</option>
								<option>Friends</option>
								<option>Private</option>
							</select>
							<div className="small text-muted flex-1 fw-semibold">
								<i className="fas fa-clock me-1"></i> Auto saved
							</div>
							<button className="btn btn-white btn-sm me-2">Cancel</button>
							<button className="btn btn-primary btn-sm">
								<i className="fa fa-paper-plane me-1"></i> Publish
							</button>
						</div>
					</div>
					{/* END card */}
					{/* BEGIN card */}
					<div className="card card-borderless">
						<div className="card-header bg-gray-900">
							<h4 className="card-header-title text-white">Message</h4>
							<div className="card-header-btn">
								<a href="#/" className="btn btn-xs btn-icon btn-default" data-toggle="card-expand">
									<i className="fa fa-expand"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-success" data-toggle="card-reload">
									<i className="fa fa-redo"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-warning" data-toggle="card-collapse">
									<i className="fa fa-minus"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-danger" data-toggle="card-remove">
									<i className="fa fa-times"></i>
								</a>
							</div>
						</div>
						<div className="px-3 py-2 d-flex align-items-center">
							<div className="d-flex gap-2 align-items-center">
								<div>
									<img
										src="/color-admin/admin/react/assets/img/user/user-16.jpg"
										alt=""
										width="42"
										className="rounded-circle"
									/>
								</div>
								<div className="flex-1">
									<div className="fw-bold">Sean Ngu</div>
									<div className="fw-semibold small text-muted lh-sm d-flex align-items-center">
										<i className="fa fa-circle text-success fs-6px me-5px mt-1px"></i> online
									</div>
								</div>
							</div>
							<div className="ms-auto d-flex gap-2">
								<button className="btn btn-lg btn-icon btn-default rounded-1">
									<iconify-icon icon="solar:phone-calling-bold"></iconify-icon>
								</button>
								<button className="btn btn-lg btn-icon btn-default rounded-1">
									<iconify-icon icon="solar:videocamera-record-bold"></iconify-icon>
								</button>
								<button className="btn btn-lg btn-icon btn-default rounded-1">
									<iconify-icon icon="solar:users-group-two-rounded-bold"></iconify-icon>
								</button>
								<button className="btn btn-lg btn-icon btn-default rounded-1">
									<iconify-icon icon="solar:info-circle-bold"></iconify-icon>
								</button>
							</div>
						</div>
						<div className="card-body p-0">
							<div className="h-300px p-3 bg-light" data-scrollbar="true" data-wheel-propagation="true">
								<div className="d-flex gap-3 mb-4">
									<div>
										<img
											src="/color-admin/admin/react/assets/img/user/user-15.jpg"
											alt=""
											width="30"
											className="rounded-circle my-4px"
										/>
									</div>
									<div>
										<div className="bg-component py-2 px-10px rounded fw-semibold">
											{' '}
											Hey Sean — pushed UI updates for dashboard cards.{' '}
										</div>
										<div className="ms-1 mt-1 small fs-10px fw-bold text-body text-opacity-50">
											10:21 AM
										</div>
									</div>
								</div>
								<div className="d-flex gap-3 mb-4 justify-content-end">
									<div className="text-end">
										<div className="bg-primary text-white py-2 px-10px rounded fw-semibold">
											Got it. Reviewing spacing & charts now.{' '}
										</div>
										<div className="me-1 mt-1 small fs-10px fw-bold text-body text-opacity-50">
											10:22 AM
										</div>
									</div>
									<div>
										<img
											src="/color-admin/admin/react/assets/img/user/user-16.jpg"
											alt=""
											width="30"
											className="rounded-circle my-4px"
										/>
									</div>
								</div>
								<div className="d-flex gap-3 mb-4">
									<div>
										<img
											src="/color-admin/admin/react/assets/img/user/user-6.jpg"
											alt=""
											width="30"
											className="rounded-circle my-4px"
										/>
									</div>
									<div>
										<div className="bg-component py-2 px-10px rounded fw-semibold mb-2">
											Also uploaded revised typography scale.
										</div>
										<div className="bg-component py-2 px-10px rounded fw-semibold d-inline-block">
											<strong>dashboard.fig</strong>
											<p className="small mb-2">Figma File</p>
											<button className="btn btn-default btn-xs">Preview</button>
										</div>
										<div className="ms-1 mt-1 small fs-10px fw-bold text-body text-opacity-50">
											10:24 AM
										</div>
									</div>
								</div>
								<div className="d-flex gap-3 mb-4 justify-content-end">
									<div className="text-end">
										<div className="bg-primary text-white py-2 px-10px rounded fw-semibold">
											Nice. Cards feel cleaner now 👍
										</div>
										<div className="me-1 mt-1 small fs-10px fw-bold text-body text-opacity-50">
											10:30 AM
										</div>
									</div>
									<div>
										<img
											src="/color-admin/admin/react/assets/img/user/user-16.jpg"
											alt=""
											width="30"
											className="rounded-circle my-4px"
										/>
									</div>
								</div>
								<div className="d-flex gap-3 mb-4">
									<div>
										<img
											src="/color-admin/admin/react/assets/img/user/user-8.jpg"
											alt=""
											width="30"
											className="rounded-circle my-4px"
										/>
									</div>
									<div>
										<div className="bg-component py-2 px-10px rounded fw-semibold mb-2">
											I tweaked notification badges & compact spacing.
										</div>
										<div className="ms-1 mt-1 small fs-10px fw-bold text-body text-opacity-50">
											10:33 AM
										</div>
									</div>
								</div>
								<div className="d-flex gap-3 mb-4 justify-content-end">
									<div className="text-end">
										<div className="bg-primary text-white py-2 px-10px rounded fw-semibold">
											Looks solid. Will push to staging.
										</div>
										<div className="me-1 mt-1 small fs-10px fw-bold text-body text-opacity-50">
											10:35 AM
										</div>
									</div>
									<div>
										<img
											src="/color-admin/admin/react/assets/img/user/user-16.jpg"
											alt=""
											width="30"
											className="rounded-circle my-4px"
										/>
									</div>
								</div>
								<div className="d-flex gap-3 mb-4">
									<div>
										<img
											src="/color-admin/admin/react/assets/img/user/user-5.jpg"
											alt=""
											width="30"
											className="rounded-circle my-4px"
										/>
									</div>
									<div className="text-muted fst-italic small fw-semibold py-2">John is typing…</div>
								</div>
							</div>
						</div>
						<div className="card-footer bg-white">
							<div className="input-group">
								<span className="input-group-text bg-light border-0">
									<i className="fa fa-paperclip"></i>
								</span>
								<input className="form-control bg-light border-0" placeholder="Type your message..." />
								<button className="btn btn-primary px-4"> Send </button>
							</div>
							<div className="d-flex mt-2 small text-muted justify-content-between">
								<span>Enter to send</span>
								<span>Online: 3</span>
							</div>
						</div>
					</div>
					{/* END card */}
					<br />
				</div>
				{/* END col-8 */}
				{/* BEGIN col-4 */}
				<div className="col-xl-4 d-flex flex-column gap-3">
					{/* BEGIN card */}
					<div className="card card-borderless">
						<div className="card-header bg-gray-900">
							<h4 className="card-header-title text-white">Analytics Details</h4>
							<div className="card-header-btn">
								<a href="#/" className="btn btn-xs btn-icon btn-default" data-toggle="card-expand">
									<i className="fa fa-expand"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-success" data-toggle="card-reload">
									<i className="fa fa-redo"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-warning" data-toggle="card-collapse">
									<i className="fa fa-minus"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-danger" data-toggle="card-remove">
									<i className="fa fa-times"></i>
								</a>
							</div>
						</div>
						<div className="table-responsive">
							<table className="table table-card align-middle mb-0">
								<thead>
									<tr>
										<th>Metric</th>
										<th width="130">Value</th>
										<th width="100">Trend</th>
									</tr>
								</thead>
								<tbody className="table-py-10px fw-semibold">
									<tr>
										<td>Unique Visitors</td>
										<td>
											13,203
											<span className="small text-success">
												<i className="fa fa-caret-up"></i>8.2%
											</span>
										</td>
										<td>
											<div id="sparkline-unique-visitor"></div>
										</td>
									</tr>
									<tr>
										<td>Bounce Rate</td>
										<td>
											28.2%
											<span className="small text-danger">
												<i className="fa fa-caret-down"></i>2.1%
											</span>
										</td>
										<td>
											<div id="sparkline-bounce-rate"></div>
										</td>
									</tr>
									<tr>
										<td>Page Views</td>
										<td>
											1.23m
											<span className="small text-success">
												<i className="fa fa-caret-down"></i>12.4%
											</span>
										</td>
										<td>
											<div id="sparkline-total-page-views"></div>
										</td>
									</tr>
									<tr>
										<td>Avg Time</td>
										<td>
											03:45
											<span className="small text-success">
												<i className="fa fa-caret-up"></i>4.8%
											</span>
										</td>
										<td>
											<div id="sparkline-avg-time-on-site"></div>
										</td>
									</tr>
									<tr>
										<td>New Visits</td>
										<td>40.5%</td>
										<td>
											<div id="sparkline-new-visits"></div>
										</td>
									</tr>
									<tr>
										<td>Returning</td>
										<td>
											73.4%
											<span className="small text-success">
												<i className="fa fa-caret-up"></i>5.6%
											</span>
										</td>
										<td>
											<div id="sparkline-return-visitors"></div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
					{/* END card */}
					{/* BEGIN card */}
					<div className="card card-borderless">
						<div className="card-header bg-gray-900">
							<h4 className="card-header-title text-white">Visitors User Agent</h4>
							<div className="card-header-btn">
								<a href="#/" className="btn btn-xs btn-icon btn-default" data-toggle="card-expand">
									<i className="fa fa-expand"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-success" data-toggle="card-reload">
									<i className="fa fa-redo"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-warning" data-toggle="card-collapse">
									<i className="fa fa-minus"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-danger" data-toggle="card-remove">
									<i className="fa fa-times"></i>
								</a>
							</div>
						</div>
						<div className="card-body">
							<div className="row text-center mb-3">
								<div className="col">
									<div className="fw-bold fs-5">58%</div>
									<small className="text-muted">Mobile</small>
								</div>
								<div className="col">
									<div className="fw-bold fs-5">34%</div>
									<small className="text-muted">Desktop</small>
								</div>
								<div className="col">
									<div className="fw-bold fs-5">8%</div>
									<small className="text-muted">Tablet</small>
								</div>
							</div>
							<div id="donut-chart" className="h-200px"></div>
						</div>
					</div>
					{/* END card */}
					{/* BEGIN card */}
					<div className="card card-borderless">
						<div className="card-header bg-gray-900">
							<h4 className="card-header-title text-white">Todo List</h4>
							<div className="card-header-btn">
								<a href="#/" className="btn btn-xs btn-icon btn-default" data-toggle="card-expand">
									<i className="fa fa-expand"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-success" data-toggle="card-reload">
									<i className="fa fa-redo"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-warning" data-toggle="card-collapse">
									<i className="fa fa-minus"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-danger" data-toggle="card-remove">
									<i className="fa fa-times"></i>
								</a>
							</div>
						</div>
						<div className="list-group list-group-flush">
							<div className="list-group-item d-flex align-items-center">
								<div className="form-check m-0 flex-fill">
									<input className="form-check-input" id="tdCheckbox1" type="checkbox" value="" />
									<label className="form-check-label ps-2" htmlFor="tdCheckbox1">
										<span className="d-block fw-bold form-check-line-through">
											Design new landing page
										</span>
										<span className="d-block fw-semibold small text-secondary">
											Create a modern hero section and update call-to-action.
										</span>
									</label>
								</div>
								<div>
									<a href="#/" className="text-decoration-none">
										<iconify-icon
											icon="solar:question-circle-bold-duotone"
											className="fs-3"
										></iconify-icon>
									</a>
								</div>
							</div>
							<div className="list-group-item d-flex align-items-center">
								<div className="form-check m-0 flex-fill">
									<input className="form-check-input" id="tdCheckbox2" type="checkbox" value="" />
									<label className="form-check-label ps-2" htmlFor="tdCheckbox2">
										<span className="d-block fw-bold form-check-line-through">
											Prepare presentation slides
										</span>
										<span className="d-block fw-semibold small text-secondary">
											Add product roadmap and new feature highlights.
										</span>
									</label>
								</div>
								<div>
									<a href="#/" className="text-decoration-none">
										<iconify-icon
											icon="solar:question-circle-bold-duotone"
											className="fs-3"
										></iconify-icon>
									</a>
								</div>
							</div>
							<div className="list-group-item d-flex align-items-center">
								<div className="form-check m-0 flex-fill">
									<input className="form-check-input" id="tdCheckbox3" type="checkbox" value="" />
									<label className="form-check-label ps-2" htmlFor="tdCheckbox3">
										<span className="d-block fw-bold form-check-line-through">
											Team meeting at 3 PM
										</span>
										<span className="d-block fw-semibold small text-secondary">
											Discuss Q4 goals and assign tasks.
										</span>
									</label>
								</div>
								<div>
									<a href="#/" className="text-decoration-none">
										<iconify-icon
											icon="solar:question-circle-bold-duotone"
											className="fs-3"
										></iconify-icon>
									</a>
								</div>
							</div>
							<div className="list-group-item d-flex align-items-center">
								<div className="form-check m-0 flex-fill">
									<input className="form-check-input" id="tdCheckbox4" type="checkbox" value="" />
									<label className="form-check-label ps-2" htmlFor="tdCheckbox4">
										<span className="d-block fw-bold form-check-line-through">
											<b className="text-warning">!!</b> Send invoice to client
										</span>
										<span className="d-block fw-semibold small text-secondary">
											Include last month’s hours and additional revisions.
										</span>
									</label>
								</div>
								<div>
									<a href="#/" className="text-decoration-none">
										<iconify-icon
											icon="solar:question-circle-bold-duotone"
											className="fs-3"
										></iconify-icon>
									</a>
								</div>
							</div>
						</div>
					</div>
					{/* END card */}
					{/* BEGIN card */}
					<div className="card card-borderless">
						<div className="card-header bg-gray-900">
							<h4 className="card-header-title text-white">World Visitors</h4>
							<div className="card-header-btn">
								<a href="#/" className="btn btn-xs btn-icon btn-default" data-toggle="card-expand">
									<i className="fa fa-expand"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-success" data-toggle="card-reload">
									<i className="fa fa-redo"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-warning" data-toggle="card-collapse">
									<i className="fa fa-minus"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-danger" data-toggle="card-remove">
									<i className="fa fa-times"></i>
								</a>
							</div>
						</div>
						<div className="card-body p-0 bg-gray-800 rounded-bottom">
							<div id="world-map" className="h-300px"></div>
						</div>
					</div>
					{/* END card */}
					{/* BEGIN card */}
					<div className="card card-borderless">
						<div className="card-header bg-gray-900">
							<h4 className="card-header-title text-white">Calendar</h4>
							<div className="card-header-btn">
								<a href="#/" className="btn btn-xs btn-icon btn-default" data-toggle="card-expand">
									<i className="fa fa-expand"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-success" data-toggle="card-reload">
									<i className="fa fa-redo"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-warning" data-toggle="card-collapse">
									<i className="fa fa-minus"></i>
								</a>
								<a href="#/" className="btn btn-xs btn-icon btn-danger" data-toggle="card-remove">
									<i className="fa fa-times"></i>
								</a>
							</div>
						</div>
						<div className="card-body p-1">
							<div
								id="datepicker-inline"
								className="datepicker-full-width overflow-y-scroll position-relative"
							>
								<div></div>
							</div>
						</div>
					</div>
					{/* END card */}
				</div>
				{/* END col-4 */}
			</div>
			{/* END row */}
		</div>
	);
}

export default DashboardV1;
